import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameTypes, GameType } from '../../hooks/useGameTypes';
import GameConfirmationModal from '../../components/GameConfirmationModal/GameConfirmationModal';
import './Games.css';

// Map numeric levels to backend difficulty levels
const DIFFICULTY_MAP: Record<string, string> = {
  '1': 'easy',
  '2': 'medium',
  '3': 'hard'
};

// Add the color pairs at the top of the file
const GAME_COLORS: Record<string, [string, string]> = {
  'apocalypse': ['#890101', '#ff4d4d'],
  'detective': ['#2c0152', '#9b4dff'],
  'space_explorer': ['#013359', '#4d79ff'],
  'hitler_assassination': ['green', 'black'],
  'fairy_realm': ['yellow', 'black'],
  'default': ['#890101', '#ff4d4d'] // Fallback colors
};

interface GameWithKey extends GameType {
  key: string;
}

const Games: React.FC = () => {
  const navigate = useNavigate();
  const { gameTypes, isLoading, error } = useGameTypes();
  const [selectedGame, setSelectedGame] = useState<string | null>(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('2');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Get all available games that match the search
  const searchedGames = useMemo((): GameWithKey[] => {
    if (!gameTypes) return [];
    
    return Object.entries(gameTypes)
      .filter(([, gameData]) => gameData.availability === 1)
      .filter(([, game]) => {
        const searchLower = searchQuery.toLowerCase();
        return (
          game.name.toLowerCase().includes(searchLower) ||
          game.short_description.toLowerCase().includes(searchLower)
        );
      })
      .map(([key, game]) => ({ key, ...game }));
  }, [gameTypes, searchQuery]);

  // Update selected game when search changes
  useEffect(() => {
    if (searchedGames.length > 0) {
      const currentGameInSearch = searchedGames.find(game => game.key === selectedGame);
      if (!currentGameInSearch) {
        setSelectedGame(searchedGames[0].key);
      }
    } else {
      setSelectedGame(null);
    }
  }, [searchQuery, searchedGames, selectedGame]);

  // Initialize first game when data loads
  useEffect(() => {
    if (gameTypes && !selectedGame && !searchQuery) {
      const availableGames = Object.entries(gameTypes)
        .filter(([, gameData]) => gameData.availability === 1)
        .map(([key]) => key);

      if (availableGames.length > 0) {
        setSelectedGame(availableGames[0]);
      }
    }
  }, [gameTypes, selectedGame, searchQuery]);

  // Get colors for the selected game
  const [color1, color2] = selectedGame ? (GAME_COLORS[selectedGame] || GAME_COLORS.default) : GAME_COLORS.default;

  if (isLoading) {
    return <div className="loading">Loading games...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  // Filter out the selected game from the grid
  const filteredGames = searchedGames.filter(game => game.key !== selectedGame);
  const featuredGame = selectedGame && gameTypes?.[selectedGame];

  const handleGameSelect = (gameKey: string) => {
    setSelectedGame(gameKey);
    setSelectedDifficulty('2');
  };

  const handleDifficultyChange = (level: string) => {
    setSelectedDifficulty(level);
  };

  const handlePlayClick = () => {
    if (!selectedGame) return;
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalConfirm = () => {
    if (!selectedGame) return;
    setIsModalOpen(false);
    navigate(`/game/${selectedGame}`, {
      state: {
        difficulty: DIFFICULTY_MAP[selectedDifficulty],
        gameType: selectedGame
      }
    });
  };

  return (
    <div className="games-page">
      {/* Search Bar */}
      <div className="search-bar">
        <i className="lni lni-search-alt"></i>
        <input
          type="text"
          placeholder="Search games..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Main Display */}
      <div className="main-display">
        {featuredGame && (
          <div className="main-display-grid">
            {/* Main Game Content */}
            <div 
              className={`active-game ${selectedGame}`}
              style={{
                '--game-gradient-start': color1,
                '--game-gradient-end': color2,
              } as React.CSSProperties}
            >
              {/* Difficulty Selector */}
              <div className="game-info">
              <div className="platform">
                  <span>{featuredGame.name}</span>
                </div>
                <div className="genre-platform">
                  <div className="genre">
                    <span>Game Genre</span>
                    <h4>{featuredGame.genre}</h4>
                  </div>
                  <div className="divider"></div>
                  <div className="platform-info">
                    <span>Platform</span>
                    <i className="material-icons">windows</i>
                  </div>
                </div>

                <div className="play-more">
                  <div className="price-card">Select Difficulty</div>
                  <div className="difficulty-options">
                    {['1', '2', '3'].map((level) => (
                      <label key={level}>
                        <input
                          type="radio"
                          name="difficulty"
                          value={level}
                          checked={selectedDifficulty === level}
                          onChange={() => handleDifficultyChange(level)}
                        />
                        <span>{level}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Game Media Section */}
              <div className="game-media">
                {featuredGame.intro_video_url ? (
                  <video
                    key={selectedGame}
                    className="game-video-bg"
                    autoPlay
                    loop
                    muted
                    playsInline
                    poster={featuredGame.image_url || "/e.png"}
                    onError={(e) => {
                      const target = e.target as HTMLVideoElement;
                      target.style.display = 'none';
                    }}
                  >
                    <source src={featuredGame.intro_video_url} type="video/mp4" />
                    <source src={featuredGame.intro_video_url} type="video/webm" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <img 
                    key={selectedGame}
                    src={featuredGame.image_url || "/e.png"} 
                    alt={featuredGame.name} 
                    className="game-video-bg" 
                  />
                )}
                <div className="game-media-content">
                  <h3>{selectedGame ? selectedGame.replace(/_/g, ' ').replace(/\b\w/g, (char: string) => char.toUpperCase()) : ''}</h3>
                  <p>{featuredGame.short_description}</p>
                  <div className="game-media-footer">
                    <div className="rating">
                      <i className="lni lni-star-filled"></i>
                      {featuredGame.rating}
                    </div>
                    <button className="play-btn" onClick={handlePlayClick}>
                      Play <i className="fas fa-play"></i>
                    </button>
                  </div>
                </div>
                <div className="age-rating">6+</div>
              </div>
            </div>

            {/* Description Card - Hidden on mobile */}
            <div 
              className="description-card"
              style={{
                '--game-gradient-start': color1,
                '--game-gradient-end': color2,
              } as React.CSSProperties}
            >
              <h3>Game Description</h3>
              <div className="description-content">
                <p>{featuredGame.long_description}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Games Grid */}
      <div className="games-grid">
        {filteredGames.map((game) => (
          <div
            key={game.key}
            className="game-card"
            onClick={() => handleGameSelect(game.key)}
          >
            <div className="game-card-media">
              <img src={game.image_url} alt={game.name} />
            </div>
            <div className="game-card-content">
              <h3>{game.name}</h3>
              <p className="genre">{game.genre}</p>
              <p className="description">{game.short_description}</p>
              <div className="game-card-footer">
                <span className="rating">
                  <i className="lni lni-star-filled"></i>
                  {game.rating}
                </span>
                <span className="status">
                  {game.availability === 1 ? 'Available' : 'Coming Soon'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Game Confirmation Modal */}
      {selectedGame && featuredGame && (
        <GameConfirmationModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onConfirm={handleModalConfirm}
          gameTitle={featuredGame.name}
          gameKey={selectedGame}
          description={featuredGame.long_description}
          difficulty={DIFFICULTY_MAP[selectedDifficulty]}
          videoUrl={featuredGame.intro_video_url}
          imageUrl={featuredGame.image_url}
          backgroundImageUrl={featuredGame.background_image_url}
        />
      )}
    </div>
  );
};

export default Games; 